import styles from './BookedFlow.module.scss';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  InlineNotification,
  NotificationType,
  Select,
  TSelectOption,
} from '@spotted-zebra-uk/ui-components';
import { BookingDetails, RescheduleSeat } from '../../api/bookingApi.types';
import {
  formatDayFull,
  formatSlotRange,
  listTimeZones,
  timeZoneLabel,
} from '../../timezone';
import { useFocusOnMount } from '../../useFocusOnMount';
import SlotPicker from '../SlotPicker/SlotPicker';
import { ScreenNotice } from './ActionScreen';

interface ReschedulePickStateProps {
  booking: BookingDetails;
  timeZone: string;
  onTimeZoneChange: (timeZone: string) => void;
  /** Why the last attempt to move did not go through, if it did not. */
  notice?: ScreenNotice | null;
  onContinue: (seat: RescheduleSeat) => void;
  /** None of the offered times work: ask the hiring team for new ones. */
  onCantDoAny: () => void;
  onBack: () => void;
}

/**
 * The reschedule page: the other times the booking read offered, in the
 * same calendar the candidate booked with, with the current interview
 * above. Nothing here is held: the move is one call, made after the
 * candidate confirms on the next screen.
 */
const ReschedulePickState: FC<ReschedulePickStateProps> = ({
  booking,
  timeZone,
  onTimeZoneChange,
  notice,
  onContinue,
  onCantDoAny,
  onBack,
}) => {
  const { t } = useTranslation();
  const headingRef = useFocusOnMount<HTMLHeadingElement>(true);
  const [selectedSeatId, setSelectedSeatId] = useState<string | null>(null);
  const selectedSeat =
    booking.otherTimes.find(seat => seat.seatId === selectedSeatId) ?? null;

  const timeZoneOptions = useMemo<TSelectOption<string>[]>(() => {
    const zones = listTimeZones();
    const withCurrent = zones.includes(timeZone) ? zones : [timeZone, ...zones];
    const timesShown = [
      booking.startsAt,
      ...booking.otherTimes.map(seat => seat.startsAt),
    ];
    return withCurrent.map(zone => ({
      value: zone,
      label: timeZoneLabel(zone, timesShown),
    }));
  }, [timeZone, booking.startsAt, booking.otherTimes]);
  const timeZoneValue = timeZoneOptions.find(o => o.value === timeZone);

  const summary = selectedSeat
    ? t('publicScheduling.youPicked', {
        day: formatDayFull(selectedSeat.startsAt, timeZone),
        range: formatSlotRange(selectedSeat, timeZone),
      })
    : t('publicScheduling.otherTimesOnOffer', {
        count: booking.otherTimes.length,
      });

  return (
    <section
      className={styles.card}
      aria-labelledby="public-scheduling-reschedule"
    >
      <div className={styles.cardHeader}>
        <h1
          id="public-scheduling-reschedule"
          className={styles.cardTitle}
          ref={headingRef}
          tabIndex={-1}
        >
          {t('publicScheduling.rescheduleTitle')}
        </h1>
        <p className={styles.cardSubline}>
          {t('publicScheduling.rescheduleSubline', {
            day: formatDayFull(booking.startsAt, timeZone),
            range: formatSlotRange(booking, timeZone),
          })}
        </p>
      </div>

      <div className={styles.meta}>
        <span className={styles.metaLabel} id="public-scheduling-reschedule-tz">
          {t('publicScheduling.timeZone')}
        </span>
        <div className={styles.timeZoneSelect}>
          <Select
            id="public-scheduling-reschedule-tz-select"
            options={timeZoneOptions}
            value={timeZoneValue}
            ariaLabel={t('publicScheduling.yourTimeZone')}
            menuPosition="fixed"
            hasClearIndicator={false}
            onChange={option => option.value && onTimeZoneChange(option.value)}
          />
        </div>
      </div>

      {notice && (
        <div className={styles.cardNotice}>
          <InlineNotification
            notificationType={NotificationType.ERROR}
            action={
              notice.action && (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={notice.action.onClick}
                >
                  {notice.action.label}
                </Button>
              )
            }
          >
            <p>{notice.text}</p>
          </InlineNotification>
        </div>
      )}

      <div className={styles.picker}>
        <SlotPicker
          slots={booking.otherTimes}
          timeZone={timeZone}
          selectedSeatId={selectedSeatId}
          onSelect={setSelectedSeatId}
        />
      </div>

      <div className={styles.footer}>
        <div className={styles.summary}>
          <span className={styles.summaryText}>{summary}</span>
          <button
            type="button"
            className={styles.backLink}
            onClick={onCantDoAny}
          >
            {t('publicScheduling.cantDoAnyDay')}
          </button>
          <button type="button" className={styles.backLink} onClick={onBack}>
            {t('publicScheduling.keepThisInterview')}
          </button>
        </div>
        <Button
          variant="primary"
          size="medium"
          className={styles.continue}
          disabled={!selectedSeat}
          onClick={() => selectedSeat && onContinue(selectedSeat)}
        >
          {selectedSeat
            ? t('publicScheduling.continueWithTime')
            : t('publicScheduling.pickATime')}
        </Button>
      </div>
    </section>
  );
};

export default ReschedulePickState;
